/**
 * Copyright ©2024 Drivepoint
 */

import {closeSnackbar, enqueueSnackbar, SnackbarKey, SnackbarProvider} from "notistack";
import React, {ReactNode, useEffect} from "react";
import {Button, Icon, IconButton, Link, Typography} from "@mui/material";
import {EventBus} from "@bainbridge-growth/node-frontend";
import ModelSettings from "../../services/microsoft/ModelSettings.ts";
import DPButton from "../DPButton/DPButton.tsx";
import DPTooltip from "../DPTooltip/DPTooltip.tsx";
import DPErrorList from "./components/DPErrorList.tsx";
import ErrorCodes from "../../services/ErrorCodes.ts";

type DPNotificationProps = {
  children: ReactNode | ReactNode[];
};

type CustomAction = {
  label: string;
  onClick: React.MouseEventHandler
};

export default function DPNotificationWrapper(props: DPNotificationProps) {

  useEffect(() => {
    const  registrations = EventBus.registerMany("notification:error", "notification:success", "addin:notification:error", "addin:notification:success",
      async (event: any) => {
        const settings = await ModelSettings.get();
        const isForCurrentPlan =  event.plan_id === settings.sharepointItemId;

        if (["notification:success", "addin:notification:success"].includes(event.type) && isForCurrentPlan) { 
          enqueueSnackbar(event.message, {autoHideDuration: 6000, variant: "success", anchorOrigin: {horizontal: "center", vertical: "top"}}); 
        }
        if (["notification:error", "addin:notification:error"].includes(event.type) && isForCurrentPlan) {
          enqueueSnackbar(renderCustomErrorMessage(event?.code, event?.data) ?? event.message, {
            variant: "error",
            anchorOrigin: {horizontal: "center", vertical: "top"},
            persist: true,
            action: (snackbarId) =>
              <>
                {event?.action && renderCustomAction(event?.action, snackbarId)}
                {!event?.content?.hide && renderInfoAction(event?.content)}
                {renderCloseAction(snackbarId)}
              </>
          });
        }
      });
    return () => {
      EventBus.unregister(...registrations);
    };
  }, []);

  function renderCustomErrorMessage(code: number | string, data: Record<string, any> = {}) {
    if (typeof code === "number") {
      switch (code) {
        case 409:
          return "Plan name already exists. Please choose a different name.";
        default:
          return;
      }
    }

    return ErrorCodes.getErrorForCode(code as string, data)?.detailed_message ?? ErrorCodes.getErrorForCode(code as string)?.message;
  }

  function renderCloseAction(snackBarId: SnackbarKey) {
    return  <Button color="inherit" onClick={() => { closeSnackbar(snackBarId); }}>Dismiss</Button>;
  }

  function renderCustomAction(action: CustomAction, snackBarId: SnackbarKey) {
    return <DPButton label={action?.label ?? "Action"} onClick={(event) => {
      action?.onClick(event);
    }} />;
  }

  function renderInfoAction(content: Record<string, any>) {
    return   <DPTooltip triggerEl={<DPButton label="Info" variant="text" />}>
      <Typography variant="h6">{content?.title ?? "Failed To Update"}</Typography>
      {content?.message && <Typography variant="body2">{content?.message}</Typography>}
      {content?.type === "list" && <DPErrorList items={content?.items}/>}
      {!content &&
        <>
          <Typography variant="body2">
            Something went wrong. Please try again.
            If the issue persists please contact
            <Link sx={{marginLeft: "5px"}}
              target="_blank"
              href="https://docs.drivepoint.io/help/v/help-1"
              underline="none">support</Link>.
          </Typography>
        </>
      }
    </DPTooltip>;
  }

  return <SnackbarProvider maxSnack={5} preventDuplicate>
    {props.children}
  </SnackbarProvider>;
}
